import { memo, useRef, useState, type ChangeEvent, type FocusEvent, type KeyboardEvent, type MouseEvent } from 'react';
import { Input } from 'antd';
import { PlusCircleOutlined } from '@ant-design/icons';
import RowOptionsMenu from '../../../../shared/components/RowOptionsMenu';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import type { ContactGroup } from '../../../../types/InfrastructureManagerTypes';
import { ContactGroupAction } from '../../../../enums/contactActions';
import styles from './ContactGroupPanel.module.scss';

interface Props {
  group: ContactGroup;
  isOpen: boolean;
  onToggle: (groupId: number) => void;
  onDelete: (groupId: number, anchor: HTMLElement | null) => void;
  onAddContact: (groupId: number) => void;
  onRename: (groupId: number, name: string) => void;
}

const GROUP_OPTIONS = [
  { id: ContactGroupAction.RENAME, label: 'RENAME GROUP' },
  { id: ContactGroupAction.DELETE, label: 'DELETE GROUP' },
];

const ContactGroupPanelComponent = ({ group, isOpen, onToggle, onDelete, onAddContact, onRename }: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(group.name);
  // Closing unmounts the input, which can fire blur; this stops that blur saving a second time.
  const isClosedRef = useRef(false);

  const startRename = () => {
    setDraftName(group.name);
    isClosedRef.current = false;
    setIsEditing(true);
  };

  const saveName = () => {
    if (isClosedRef.current) return;
    isClosedRef.current = true;
    setIsEditing(false);
    const name = draftName.trim();
    if (name && name !== group.name) onRename(group.id, name);
  };

  const cancelRename = () => {
    isClosedRef.current = true;
    setIsEditing(false);
  };

  const handleToggleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onToggle(group.id);
  };

  const handleHeaderClick = () => onToggle(group.id);

  const handleDraftChange = (event: ChangeEvent<HTMLInputElement>) => setDraftName(event.target.value);

  const stopClick = (event: MouseEvent<HTMLElement>) => event.stopPropagation();

  const selectInputText = (event: FocusEvent<HTMLInputElement>) => event.target.select();

  const handleRenameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') cancelRename();
  };

  const handleAddContact = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onAddContact(group.id);
  };

  const handleMenuSelect = (action: string, anchor: HTMLElement | null) => {
    if (action === ContactGroupAction.RENAME) startRename();
    if (action === ContactGroupAction.DELETE) onDelete(group.id, anchor);
  };

  return (
    <header className={styles.header} onClick={handleHeaderClick}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={isOpen}
        aria-label={isEditing ? `Toggle ${group.name}` : undefined}
        onClick={handleToggleClick}
      >
        <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} aria-hidden="true" />
        {isEditing ? null : <span className={styles.name}>{group.name}</span>}
      </button>
      {isEditing ? (
        <Input
          autoFocus
          aria-label="Group name"
          value={draftName}
          className={styles.renameInput}
          onChange={handleDraftChange}
          onClick={stopClick}
          onPressEnter={saveName}
          onFocus={selectInputText}
          onBlur={saveName}
          onKeyDown={handleRenameKeyDown}
        />
      ) : null}
      <button type="button" className={styles.addButton} aria-label={`Add contact to ${group.name}`} onClick={handleAddContact}>
        <PlusCircleOutlined aria-hidden="true" />
      </button>
      {/* Ungrouped is the system group: the BE rejects renaming or deleting it. */}
      <ConditionalRender visible={!group.isUngrouped}>
        <span className={styles.menu}>
          <RowOptionsMenu options={GROUP_OPTIONS} header="GROUP OPTIONS" onSelect={handleMenuSelect} />
        </span>
      </ConditionalRender>
    </header>
  );
};

export const ContactGroupPanel = memo(ContactGroupPanelComponent);
